"use client";

import styles from "./Feature.module.scss";

// Helpers
import { forwardRef } from "react";
import { Divider, Flex } from "@/app/[lang]/components/ui/AntdLite";
import Link from "next/link";
import DOMPurify from "@/app/[lang]/utils/dompurify";
import Image from "../../shared/Image";

// Components
import type { FeatureProps } from "./interface";
import { Video } from "@/app/[lang]/components/shared/Video";
import VideoEmbed from "@/app/[lang]/components/VideoEmbed";

// Utils
import { useHrefFromPageOrURL } from "@/app/[lang]/utils/useHrefFromPageOrURL";
import classnames from "classnames";
import {getSafeHeading} from "@/app/[lang]/utils/api-helpers";

const Feature = forwardRef<HTMLDivElement, FeatureProps>((props, ref) => {
  const { title, description, newTab, media, hidden, headingLevel } = props;
  const href = useHrefFromPageOrURL( props );

  if ( !!hidden ) {
    return null;
  }

  const TitleTag = getSafeHeading(headingLevel, "h3");

  return (
    <Flex justify="center" className={styles.wrapper} ref={ ref }>
      <Link
        href={href ?? "#"}
        target={newTab ? "_blank" : "_self"}
        className={styles.anchor}
      >
        <Flex vertical className={styles.content}>
          { media?.data ?
            <div className={ styles.background }>
              { media?.data?.attributes?.mime?.startsWith("video") ?
                  <Video data={media?.data}/> :
                  <Image
                      data={media.data}
                      fill={true}
                      alt="PPC Blue Romania"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
              }
            </div> :
            <VideoEmbed url={props.media_embedded}/>
          }

          <div style={{ position: "relative", zIndex: 1 }}>
            <TitleTag className={ classnames( styles.title, 'feature-title' ) } dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
            <Divider className={styles.divider} />
            <div className={ classnames( styles.description, 'feature-description' ) } dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
          </div>

          <div className={styles.overlay} />
        </Flex>
      </Link>
    </Flex>
  );
});

Feature.displayName = 'Feature'

export default Feature;
